'use client'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight, CheckCircle2, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { Button } from '../ui'
import { useProductAnalytics } from '../../hooks/useProductAnalytics'
import { INDIAN_STATES_COMPLETE } from '../../lib/indian-states'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
import { districtsForState, citiesForState, stateIdByName, districtIdByName, cityIdByName } from '../../lib/geo/resolve'

/* Single source of the user auth UI — mobile + password (Build 1 credential model),
   hitting the EXISTING endpoints /api/auth/login and /api/auth/register. Rendered by
   BOTH the /login page and the action-gate modal, so there is no parallel auth UI. */

export type AuthMode = 'login' | 'register'
export type AuthedUser = { id: string; name?: string; email?: string; role?: string }

export default function AuthForms({
  initialMode = 'login',
  onAuthed,
}: {
  initialMode?: AuthMode
  onAuthed?: (user: AuthedUser) => void
}) {
  const track = useProductAnalytics()
  const [mode, setMode] = useState<AuthMode>(initialMode)
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // Registrant location (mirrors mobile signup) — REQUIRED on web. /api/auth/register persists
  // state/district/city to the users columns (migration 0059, optional server-side).
  const [stateVal, setStateVal] = useState('')
  const [district, setDistrict] = useState('')
  const [city, setCity] = useState('')
  // Geo pickers v2 (phase-1b) — gates CLIENT rendering only. OFF → the free-text inputs below.
  const { isFlagEnabled } = useFeatureFlags()
  const geoV2 = isFlagEnabled('geoPickersV2')
  const selStateId = geoV2 ? stateIdByName(stateVal) : null
  const [showPass, setShowPass] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // Referrals v1 — optional code, pre-filled from ?ref=CODE on the signup link. Never blocks signup.
  const [referralCode, setReferralCode] = useState('')
  // DPDP consent — REQUIRED to create an account; sent to the server, which records it in user_consents.
  const [consent, setConsent] = useState(false)
  useEffect(() => {
    try {
      const r = new URLSearchParams(window.location.search).get('ref')
      if (r) setReferralCode(r.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))
    } catch { /* no window / private mode — non-fatal */ }
  }, [])

  // ── OAuth phase-3b (Google Identity Services + Sign in with Apple JS) ──────────────────────────
  // Gated on isFlagEnabled('oauthLogin') → DARK until the flag is on (env + phase-4 must land first).
  const oauthEnabled = isFlagEnabled('oauthLogin')
  const googleBtnRef = useRef<HTMLDivElement | null>(null)
  const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID
  const APPLE_SERVICES_ID = process.env.NEXT_PUBLIC_APPLE_SERVICES_ID

  async function postOauth(provider: 'google' | 'apple', body: Record<string, unknown>) {
    setLoading(true)
    setError('')
    try {
      const res = await fetch(`/api/auth/oauth/${provider}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, consent: { accepted: true, source: 'web_signup' } }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Sign-in could not be completed. Please try again.')
        setLoading(false)
        return
      }
      if (data.user) persist(data.user)
      track({ name: 'login_completed', role: 'user', domain: 'onboarding', userId: data.user?.id })
      // Phase-4 gate: a new OAuth user (profile_completed=false) must finish phone + geo before
      // entering the app. Existing users are true → straight through via onAuthed.
      if (data.profile_completed === false) {
        window.location.assign('/complete-profile')
        return
      }
      onAuthed?.(data.user)
    } catch {
      setError('Network error. Please try again.')
    }
    setLoading(false)
  }

  async function handleApple() {
    const AppleID = (window as unknown as { AppleID?: any }).AppleID
    if (!AppleID?.auth) { setError('Apple sign-in is unavailable right now.'); return }
    try {
      const data = await AppleID.auth.signIn() // { authorization: { id_token }, user? } — popup flow
      const idToken = data?.authorization?.id_token
      if (!idToken) { setError('Apple sign-in could not be completed.'); return }
      // Apple returns the name only on the FIRST authorization.
      const name = data?.user?.name
        ? [data.user.name.firstName, data.user.name.lastName].filter(Boolean).join(' ').trim()
        : undefined
      await postOauth('apple', { identityToken: idToken, name })
    } catch {
      /* user cancelled the Apple popup — not an error to surface */
    }
  }

  // Load the provider SDKs + wire them up ONLY when the flag is on. Google renders its own branded
  // button (yields an ID token via callback); Apple JS is init'd for the custom button's signIn().
  useEffect(() => {
    if (!oauthEnabled) return
    function loadScript(src: string, onload: () => void) {
      if (document.querySelector(`script[src="${src}"]`)) { onload(); return }
      const s = document.createElement('script')
      s.src = src; s.async = true; s.onload = onload
      document.head.appendChild(s)
    }
    if (GOOGLE_CLIENT_ID) {
      loadScript('https://accounts.google.com/gsi/client', () => {
        const g = (window as unknown as { google?: any }).google
        if (!g?.accounts?.id) return
        g.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp: { credential?: string }) => {
            if (resp?.credential) void postOauth('google', { idToken: resp.credential })
          },
        })
        if (googleBtnRef.current) {
          g.accounts.id.renderButton(googleBtnRef.current, { type: 'standard', theme: 'outline', size: 'large', text: 'continue_with', width: 320 })
        }
      })
    }
    if (APPLE_SERVICES_ID) {
      loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js', () => {
        const a = (window as unknown as { AppleID?: any }).AppleID
        if (!a?.auth) return
        a.auth.init({ clientId: APPLE_SERVICES_ID, scope: 'name email', redirectURI: window.location.origin + '/login', usePopup: true })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthEnabled])

  function persist(user: AuthedUser) {
    try {
      if (user.email) localStorage.setItem('user_email', user.email)
      if (user.name) localStorage.setItem('user_name', user.name)
      localStorage.setItem('user_id', user.id)
      localStorage.setItem('user_logged_in', 'true')
    } catch { /* private mode / storage disabled — non-fatal */ }
  }

  function switchMode(next: AuthMode) {
    setMode(next)
    setError('')
    setSuccess('')
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    if (!phone || !password) {
      setError('Please enter your mobile number or email, and your password.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: phone.trim(), password }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Login failed. Check your mobile/email and password.')
        setLoading(false)
        return
      }
      persist(data.user)
      track({ name: 'login_completed', role: 'user', domain: 'onboarding', userId: data.user.id })
      onAuthed?.(data.user)
    } catch {
      setError('Network error. Please try again.')
    }
    setLoading(false)
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    if (!name || !phone || !password) {
      setError('Name, mobile number, and password are required.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (!stateVal) {
      setError('State is required.')
      return
    }
    if (!district.trim()) {
      setError('District is required.')
      return
    }
    if (!city.trim()) {
      setError('City is required.')
      return
    }
    if (!consent) {
      setError('Please agree to the Privacy Policy and Terms to create an account.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(), phone: phone.trim(), email: email.trim().toLowerCase(), password,
          state: stateVal, district: district.trim(), city: city.trim(),
          // Structured ids only when the pickers are on (server re-validates; free-text town → null id).
          ...(geoV2 ? { stateId: selStateId ?? undefined, districtId: districtIdByName(selStateId, district) ?? undefined, cityId: cityIdByName(selStateId, city) ?? undefined } : {}),
          referralCode: referralCode.trim() || undefined, consent: { accepted: true, source: 'web_signup' },
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Registration failed.')
        setLoading(false)
        return
      }
      setSuccess('Account created.')
      if (data.user) persist(data.user)
      track({ name: 'signup_completed', role: 'user', domain: 'onboarding', userId: data.user?.id })
      onAuthed?.(data.user)
    } catch {
      setError('Network error. Please try again.')
    }
    setLoading(false)
  }

  return (
    <div className="authforms">
      <div className="entry-tab-bar" role="tablist" aria-label="Authentication mode">
        <button className={mode === 'login' ? 'is-active' : ''} onClick={() => switchMode('login')} type="button">Sign in</button>
        <button className={mode === 'register' ? 'is-active' : ''} onClick={() => switchMode('register')} type="button">Create account</button>
      </div>

      {error && <div className="entry-error"><ShieldCheck size={16} /> <span>{error}</span></div>}
      {success && <div className="entry-success"><CheckCircle2 size={16} /> <span>{success}</span></div>}

      {mode === 'login' ? (
        <form className="entry-form" onSubmit={handleLogin}>
          <label className="entry-field">
            <span>Mobile number or email</span>
            <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile or you@email.com" autoComplete="username" autoFocus />
          </label>
          <label className="entry-field">
            <span>Password</span>
            <input type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" autoComplete="current-password" />
          </label>
          <div className="entry-actions">
            <Button type="submit" size="lg" disabled={loading} rightIcon={<ArrowRight size={16} />}>
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
            <Button type="button" variant="ghost" size="lg" onClick={() => setShowPass((p) => !p)} leftIcon={showPass ? <EyeOff size={16} /> : <Eye size={16} />}>
              {showPass ? 'Hide' : 'Show'}
            </Button>
          </div>
          <a href="/forgot-password" className="entry-muted">Forgot password?</a>
        </form>
      ) : (
        <form className="entry-form" onSubmit={handleRegister}>
          <label className="entry-field">
            <span>Full name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Mohammed Ahmed" autoComplete="name" autoFocus />
          </label>
          <label className="entry-field">
            <span>Mobile number</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile number" autoComplete="tel" inputMode="numeric" />
          </label>
          <label className="entry-field">
            <span>Email address <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional)</span></span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
            <small className="entry-muted" style={{ fontSize: 11.5 }}>Optional — but it's the only way to reset your password if you forget it. Add one to keep your account recoverable.</small>
          </label>
          <label className="entry-field">
            <span>State</span>
            <select
              value={stateVal}
              onChange={(e) => { setStateVal(e.target.value); if (geoV2) { setDistrict(''); setCity(''); } }}
            >
              <option value="">Select state or UT</option>
              {INDIAN_STATES_COMPLETE.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          {geoV2 ? (
            /* Pickers v2 — native <datalist> typeahead: suggestions from the dataset, but the user can
               still type an unlisted town (free-text). District cascades from state; CITY is STATE-scoped
               (not district — no city→district link in 1a) with a free-text fallback. */
            <div className="entry-field-row">
              <label className="entry-field">
                <span>District</span>
                <input type="text" list="geo-districts" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="Start typing your district" disabled={!stateVal} />
                <datalist id="geo-districts">
                  {selStateId ? districtsForState(selStateId).map((d) => <option key={d.id} value={d.name} />) : null}
                </datalist>
              </label>
              <label className="entry-field">
                <span>City / town</span>
                <input type="text" list="geo-cities" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City / town / village" autoComplete="address-level2" disabled={!stateVal} />
                <datalist id="geo-cities">
                  {selStateId ? citiesForState(selStateId).map((c) => <option key={c.id} value={c.name} />) : null}
                </datalist>
              </label>
            </div>
          ) : (
            <div className="entry-field-row">
              <label className="entry-field">
                <span>District</span>
                <input type="text" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="District" />
              </label>
              <label className="entry-field">
                <span>City</span>
                <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City / town / village" autoComplete="address-level2" />
              </label>
            </div>
          )}
          <div className="entry-field-row">
            <label className="entry-field">
              <span>Password</span>
              <input type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 characters" autoComplete="new-password" />
            </label>
            <label className="entry-field" style={{ position: 'relative' }}>
              <span>Confirm</span>
              <input className={confirmPassword && confirmPassword !== password ? 'is-invalid' : ''} type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" autoComplete="new-password" />
              <button type="button" onClick={() => setShowConfirm((p) => !p)} aria-label={showConfirm ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 8, bottom: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--f-slate)', display: 'grid', placeItems: 'center', padding: 4 }}>
                {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </label>
          </div>
          <label className="entry-field">
            <span>Referral code <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional)</span></span>
            <input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="Have a friend's code? Enter it" autoCapitalize="characters" autoComplete="off" inputMode="text" />
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.4, cursor: 'pointer' }}>
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required style={{ marginTop: 2, flex: '0 0 auto' }} />
            <span>
              I agree to the{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--f-teal, #0E7C61)', fontWeight: 600 }}>Privacy Policy</a>
              {' '}and{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--f-teal, #0E7C61)', fontWeight: 600 }}>Terms of Service</a>.
            </span>
          </label>
          <Button type="submit" size="lg" disabled={loading || !consent} rightIcon={<ArrowRight size={16} />}>
            {loading ? 'Creating account...' : 'Create account'}
          </Button>
        </form>
      )}

      {/* OAuth sign-in — DARK until the oauthLogin flag is on. Google renders its own branded button
          (ID token via callback); Apple uses a custom button → Sign in with Apple JS popup. Shared
          across login + register modes. */}
      {oauthEnabled && (
        <div className="entry-oauth" style={{ marginTop: 16, display: 'grid', gap: 10, justifyItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', color: 'var(--f-slate)', fontSize: 13 }}>
            <span style={{ flex: 1, height: 1, background: 'var(--f-border, #e5e7eb)' }} />
            or continue with
            <span style={{ flex: 1, height: 1, background: 'var(--f-border, #e5e7eb)' }} />
          </div>
          {/* Google renders its branded button into this container. */}
          <div ref={googleBtnRef} />
          {APPLE_SERVICES_ID ? (
            <button
              type="button"
              onClick={handleApple}
              disabled={loading}
              style={{ width: 320, maxWidth: '100%', height: 44, borderRadius: 8, border: 'none', background: '#000', color: '#fff', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              aria-label="Continue with Apple"
            >
               Continue with Apple
            </button>
          ) : null}
          <p style={{ fontSize: 12, color: 'var(--f-slate)', textAlign: 'center', margin: 0, lineHeight: 1.4 }}>
            By continuing, you agree to our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--f-teal, #0E7C61)' }}>Privacy Policy</a>
            {' '}and{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--f-teal, #0E7C61)' }}>Terms</a>.
          </p>
        </div>
      )}
    </div>
  )
}
